import * as React from 'react';
import { motion } from 'framer-motion';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, Image as ImageIcon, FileText, Loader2, AlertCircle, Plus, Trash2, Calendar, HardDrive, LayoutGrid, ChevronRight, XCircle, Settings, MapPin, Lock } from 'lucide-react';
import { useUser, useAuth } from '@clerk/react';
import { platform } from '@/platform.config';
import DisclaimerModal from './components/DisclaimerModal';
import { ToolType, SymbolType, SystemType, MarkupState, ViewSettings, Project } from '@shared/types/tsk';
import Editor from './components/Editor';
import GeminiPanel from './components/GeminiPanel';
import { AccountStatus } from './components/AccountStatus';
import { useTradeSketchState } from './hooks/useTradeSketchState';
import { ProjectList } from './components/ProjectList';
import ProjectDetailsModal from './components/ProjectDetailsModal';
import { Button } from './components/ui/Button';
import { HUDOverlay } from './components/visual/HUDOverlay';
import { getAllProjects, saveProject as dbSaveProject, deleteProject as dbDeleteProject } from './utils/storage';
const SETTINGS_KEY = 'tradesketch_global_settings';
const MAX_IMAGE_DIMENSION = 1200;
const JPEG_QUALITY = 0.6;
const App: React.FC = () => {
  const {
    projects, setProjects,
    currentProjectId, setCurrentProjectId,
    image, setImage,
    markup, setMarkup,
    history, setHistory,
    redoStack, setRedoStack,
    activeTool, setActiveTool,
    activeSymbol, setActiveSymbol,
    activeSystem, setActiveSystem,
    handleSave, updateProjectDetails, createNewProject, loadProject, deleteProject,
    updateMarkup, handleUndo, handleRedo
  } = useTradeSketchState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const [viewSettings, setViewSettings] = useState<ViewSettings>({
    dynamicFading: true,
    symbolScale: 1.5,
    scaleWithPlan: false,
    showGrid: false,
    snapToGrid: true,
    gridSize: 20
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentProject = projects.find(p => p.id === currentProjectId);
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const savedProjects = await getAllProjects();
        setProjects(savedProjects);
      } catch (e) {
        console.error("Failed to load projects from IndexedDB", e);
      }
      const settings = localStorage.getItem(SETTINGS_KEY);
      if (settings) {
        try {
          setViewSettings((prev: ViewSettings) => ({ ...prev, ...JSON.parse(settings) }));
        } catch (e) { }
      }
      const disclaimerAccepted = localStorage.getItem('tradesketch_disclaimer_accepted');
      if (!disclaimerAccepted) {
        setShowDisclaimer(true);
      }
    };
    loadInitialData();
  }, []);
  useEffect(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocalhost) return;
    if (!isLoaded || !user) return;
    const logLogin = async () => {
      const loggedThisSession = sessionStorage.getItem('tradesketch_app_login_logged');
      if (loggedThisSession) return;
      try {
        const token = await getToken();
        const baseUrl = platform.hubUrl;
        await fetch(`${baseUrl}/api/telemetry/v1/log`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ type: 'app_login' })
        });
        sessionStorage.setItem('tradesketch_app_login_logged', 'true');
      } catch (e) {
        console.error('Failed to log telemetry', e);
      }
    };
    logLogin();
  }, [isLoaded, user]);
  const compressAndCreate = (file: File) => {
    const reader = new FileReader();
    reader.onerror = () => {
      setError("Failed to read the selected file. It may be corrupted.");
      setIsLoading(false);
    };
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => {
        setError("Browser failed to decode this image. Please ensure it is a valid JPG or PNG.");
        setIsLoading(false);
      };
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          const ratio = Math.min(MAX_IMAGE_DIMENSION / width, MAX_IMAGE_DIMENSION / height);
          width *= ratio;
          height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setError("Canvas rendering failed.");
          setIsLoading(false);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        try {
          const result = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
          createNewProject(result);
        } catch (storageErr) {
          setError("Image data is too large for local storage. Try a smaller plan.");
        } finally {
          setIsLoading(false);
        }
      };
      const result = e.target?.result;
      if (typeof result === 'string') {
        img.src = result;
      } else {
        setError("Invalid file data received.");
        setIsLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const isTiff = file.name.toLowerCase().endsWith('.tif') ||
        file.name.toLowerCase().endsWith('.tiff') ||
        file.type === 'image/tiff';
      if (isTiff) {
        setError("TIFF images are not supported for web sketching. Please save your plan as a JPG or PNG before importing.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      setError(null);
      setIsLoading(true);
      compressAndCreate(file);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const handleAcceptDisclaimer = () => {
    localStorage.setItem('tradesketch_disclaimer_accepted', 'true');
    setShowDisclaimer(false);
  };
  if (isAnalyzing) {
    return <GeminiPanel project={currentProject} onClose={() => setIsAnalyzing(false)} autoDownload={false} />;
  }
  return (
    <div className="flex flex-col h-[100dvh] bg-[var(--color-background)] text-[var(--color-foreground)] overflow-hidden select-none relative">
      <header className="flex items-center justify-between px-6 py-3 sm:px-4 bg-[#0a0a0b]/80 backdrop-blur-md border-b border-white/5 z-[40] shrink-0 glint-container">
        <div className="glint-sweep" />
        <div className="flex items-center gap-4">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="relative w-10 h-10 flex items-center justify-center font-black"
          >
            <img 
              src="/brand/logo-gear-only.png" 
              alt="CadOps Symbol" 
              className="w-full h-full object-contain"
            />
          </motion.div>
          <div className="flex flex-col">
            <h1 className="text-sm font-black tracking-tighter uppercase text-white leading-none">
              CADOps
            </h1>
            <p className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/40 mt-0.5">
              Practical Drafting & Fire Safety Solutions
            </p>
            {currentProject && (
              <button
                onClick={() => setIsProjectDetailsOpen(true)}
                className="flex items-center gap-1.5 text-[var(--color-primary)] group transition-all mt-1"
                data-testid="project-details-toggle"
              >
                <p className="text-[10px] font-bold truncate max-w-[120px] group-hover:text-[var(--color-primary-orange-light)]">
                  {currentProject.name}
                </p>
                <Settings size={10} className="group-hover:rotate-45 transition-transform" />
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {image ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => { handleSave(); setCurrentProjectId(null); setImage(null); }}
                className="p-2.5 text-[var(--color-primary-gray)] hover:text-[var(--color-foreground)] bg-[var(--color-foreground)]/5 hover:bg-[var(--color-foreground)]/10 rounded-xl transition-all"
                title="Go Home"
              >
                <LayoutGrid size={20} />
              </Button>
              <div className="w-px h-6 bg-[var(--color-foreground)]/10 mx-1" />
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isSaving ? 'bg-green-600/50 text-[var(--color-foreground)]/50' : 'bg-[var(--color-primary)] hover:bg-[var(--color-primary-orange-dark)] text-[var(--color-primary-black)] shadow-lg'}`}
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <HardDrive size={16} />}
                <span>{isSaving ? 'Saved' : 'Save'}</span>
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { handleSave(); setIsAnalyzing(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-[var(--color-foreground)] shadow-lg"
                data-testid="open-report-button"
              >
                <FileText size={16} /> <span>Report</span>
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-[var(--color-foreground)]/30 uppercase tracking-widest">Dashboard</span>
            </div>
          )}
        </div>
      </header >
      <main className="flex-1 relative overflow-hidden flex flex-col bg-[var(--color-background)]">
        {isLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <Loader2 className="animate-spin text-[var(--color-primary)]" size={48} />
            <p className="text-[var(--color-foreground)] font-black text-xs uppercase tracking-widest">Processing Plan...</p>
          </div>
        ) : !image ? (
          <div className="flex-1 overflow-y-auto p-6 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
            <div className="schematic-bg" />
            <section className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-primary-orange-dark)] rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden ring-1 ring-[var(--color-foreground)]/10 backdrop-blur-sm">
              <div className="relative z-10">
                <h2 className="text-3xl font-black mb-2 leading-tight tracking-tighter text-[var(--color-primary-black)]">New Sketch</h2>
                <p className="text-[var(--color-primary-black)]/70 text-sm mb-10 max-w-xs font-medium">Capture a site plan or import digital drafts to start marking up your trade layout.</p>
                <div className="flex flex-wrap gap-4">
                  <Button variant="default" onClick={() => { fileInputRef.current?.setAttribute('capture', 'environment'); fileInputRef.current?.click(); }} className="flex items-center gap-3 px-8 py-5 bg-[var(--color-background)] text-[var(--color-primary)] rounded-2xl font-black shadow-xl active:scale-95 transition-all">
                    <Camera size={22} /> <span className="text-xs uppercase tracking-[0.15em]">Capture</span>
                  </Button>
                  <Button variant="outline" onClick={() => { fileInputRef.current?.removeAttribute('capture'); fileInputRef.current?.click(); }} className="flex items-center gap-3 px-8 py-5 bg-[var(--color-primary-black)]/10 backdrop-blur-md text-[var(--color-primary-black)] border border-[var(--color-primary-orange)]/40 rounded-2xl font-black shadow-xl active:scale-95 transition-all">
                    <Plus size={22} /> <span className="text-xs uppercase tracking-[0.15em]">Import</span>
                  </Button>
                </div>
              </div>
              <div className="absolute top-[-10%] right-[-10%] opacity-10 rotate-12 scale-[2] pointer-events-none">
                <LayoutGrid size={240} />
              </div>
            </section>
            <ProjectList projects={projects} loadProject={loadProject} deleteProject={deleteProject} AccountStatus={AccountStatus} />
          </div>
        ) : (
          <Editor
            image={image}
            setImage={(newImg) => {
              setImage(newImg);
              if (currentProjectId) {
                const projectToUpdate = projects.find(p => p.id === currentProjectId);
                if (projectToUpdate) {
                  const updated = { ...projectToUpdate, image: newImg, thumbnail: newImg, lastModified: Date.now() };
                  dbSaveProject(updated).catch(() => {});
                  setProjects(prev => prev.map(p => p.id === currentProjectId ? updated : p));
                }
              }
            }}
            markup={markup}
            setMarkup={updateMarkup}
            undo={handleUndo}
            redo={handleRedo}
            canUndo={history.length > 1}
            canRedo={redoStack.length > 0}
            activeTool={activeTool}
            setActiveTool={setActiveTool}
            activeSymbol={activeSymbol}
            setActiveSymbol={setActiveSymbol}
            activeSystem={activeSystem}
            setActiveSystem={setActiveSystem}
            viewSettings={viewSettings}
            setViewSettings={setViewSettings}
          />
        )}
      </main>
      {
        error && (
          <div className="absolute bottom-24 left-1/2 -translate-x-1/2 w-[90%] max-w-sm p-4 bg-red-600 rounded-2xl flex items-center gap-3 shadow-2xl z-[100] animate-in slide-in-from-bottom-4">
            <AlertCircle size={20} />
            <p className="text-xs font-bold leading-tight">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto opacity-50 hover:opacity-100">
              <XCircle size={18} />
            </button>
          </div>
        )
      }
      {
        isProjectDetailsOpen && currentProject && (
          <ProjectDetailsModal
            project={currentProject}
            onClose={() => setIsProjectDetailsOpen(false)}
            onSave={updateProjectDetails}
          />
        )
      }
      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
      <HUDOverlay />
      {showDisclaimer && <DisclaimerModal onAccept={handleAcceptDisclaimer} />}
    </div >
  );
};

export default App;